import { createSelector, createSlice } from "@reduxjs/toolkit";
import { ensureArray, groupBy } from "../common/utils/arrayUtilities";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory, DefaultFailureAction, DefaultSuccessAction } from "../common/reducers/utilities";
import { markFetched, registerEntityBasedFetch } from "../common/hooks/initHook";

const key = n => n.internalid;

const meetingNotesSlice = createSlice({
    name: 'meetingNotes',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            const byMeeting = groupBy(ensureArray(action.payload.data), 'meetingid')
            ensureArray(action.payload.ids).forEach(meetingid => {
                if (byMeeting[meetingid]) {
                    state.data[meetingid] = byMeeting[meetingid];
                } else {
                    delete state.data[meetingid];
                }
            })
        },
        addOrUpdate: (state, action) => {
            const toUpdate = ensureArray(action.payload);
            const byMeeting = groupBy(toUpdate, 'meetingid');
            Object.entries(byMeeting).forEach(([ meetingid, notes ]) => {
                const ids = new Set(notes.map(n => key(n)));
                state.data[meetingid] = (state.data[meetingid]?.filter(n => !ids.has(key(n))) ?? []).concat(notes);
            });
        },
        remove: (state, action) => {
            const toRemove = ensureArray(action.payload);
            const byMeeting = groupBy(toRemove, 'meetingid');
            Object.entries(byMeeting).forEach(([meetingid, notes]) => {
                const toRemove = new Set(notes.map(n => key(n)));
                state.data[meetingid] = state.data[meetingid].filter(n => !toRemove.has(key(n)));
            });
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => meetingNotesSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'staffNotes', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'staffNotes');

// TODO add multi-fetch
const fetchMeetingNotes = meetingid => network => network.get('/api/v1/meetingnote/' + encodeURIComponent(meetingid), data => meetingNotesSlice.actions.set({ data, ids: [meetingid] }), visualizeFailure('get'));
export const addMeetingNote = (network, data) => network.post('/api/v1/meetingnote', data, meetingNotesSlice.actions.addOrUpdate, visualizeFailure('create'));
// #endregion

// #region selectors
const selectAllMeetingNoteData = state => state.meetingNotes.data;
const selectMeetingNotes = meetingid => createSelector([selectAllMeetingNoteData], notes => notes[meetingid] ?? []);
// #endregion

const listen = {
    meetingNoteCreated: (note, { dispatch, websocket }) => {
        markFetched(websocket, 'meeting_note', key(note))
        dispatch(meetingNotesSlice.actions.addOrUpdate(note))
    }
}

const meetingInitNotesByID = initMapFactory(fetchMeetingNotes, listen);
registerEntityBasedFetch('meeting', meetingInitNotesByID);

const cleanup = { root: meetingNotesSlice.name, all: state => Object.values(selectAllMeetingNoteData(state)).flat(), removeAll: meetingNotesSlice.actions.remove, key };
export const [useMeetingNotes] = dataHookFactory(selectMeetingNotes, meetingInitNotesByID, { cleanup });

const reducer = { name: meetingNotesSlice.name, reducer: meetingNotesSlice.reducer };
export default reducer;